import React, { useEffect, useState } from "react";
import { ChatCircleText, Heart, Flag, ShieldStar, WarningCircle, Check } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { roleLabel } from "@/lib/roles";

const CHANNEL_LABELS = [
  ["student_teacher_dm", "Inbox: Student ↔ Teacher DMs", "Students can open free-form chats with their teacher."],
  ["student_student_dm", "Inbox: Student ↔ Student DMs", "Peer chat. Default OFF for FOIP / safeguarding."],
  ["teacher_teacher_dm", "Inbox: Teacher ↔ Teacher DMs", "Teachers can DM each other in-app."],
  ["per_lesson_help", "Per-lesson \u201cI need help\u201d channel", "The chat icon under each lesson, pre-filled with the lesson name."],
  ["broadcast_admin", "Broadcasts: Super Admin → students", "Board-wide announcements from the Super Admin."],
  ["broadcast_teacher", "Broadcasts: Teacher → their class", "Class-wide announcements from teachers."],
  ["sos_enabled", "SOS / Help button (❤️)", "Crisis resources, Safety Officer routing, Ghost Mode."],
];

const PRESET_LABELS = {
  full_chat: "Full Chat", teachers_only: "Teachers-Only", course_only: "Course-Only",
  sos_only: "SOS Only", all_off: "All Off",
};

function Toggles({ settings, onChange, presets }) {
  return (
    <>
      {presets && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          {Object.entries(presets).map(([key, vals]) => (
            <button key={key} data-testid={`preset-${key}`}
              className="text-xs px-3 py-1.5 rounded-full border border-[#0A0D14] bg-white hover:bg-[#F4F4F5] active:scale-95 transition-transform font-semibold"
              onClick={() => { onChange({ ...settings, ...vals }); toast.info(`${PRESET_LABELS[key] || key} preset applied — press Save to keep it`); }}>
              {PRESET_LABELS[key] || key}
            </button>
          ))}
        </div>
      )}
      <div className="divide-y divide-[#EDEEF0]">
        {CHANNEL_LABELS.map(([key, label, hint]) => (
          <div key={key} className="flex items-center justify-between gap-4 py-3">
            <div className="min-w-0">
              <div className="font-semibold text-sm flex items-center gap-1.5">
                {key === "sos_enabled" && <Heart size={14} weight="fill" color="#DC2626" />}
                {label}
              </div>
              <div className="text-xs text-[#000000]">{hint}</div>
            </div>
            <Switch data-testid={`channel-toggle-${key}`} checked={!!settings[key]}
              onCheckedChange={(v) => onChange({ ...settings, [key]: v })} />
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between gap-4 py-3 border-t border-[#EDEEF0]">
        <div>
          <div className="font-semibold text-sm">School-hours restriction</div>
          <div className="text-xs text-[#000000]">Block Student ↔ Teacher DMs outside {settings.school_hours_start}–{settings.school_hours_end} ({settings.school_timezone})</div>
        </div>
        <Switch data-testid="channel-toggle-school_hours_only" checked={!!settings.school_hours_only}
          onCheckedChange={(v) => onChange({ ...settings, school_hours_only: v })} />
      </div>
      {settings.school_hours_only && (
        <div className="flex flex-wrap items-center gap-2 pb-3 pl-1" data-testid="school-hours-editor">
          <span className="text-xs font-semibold text-[#3F3F46]">Allowed hours:</span>
          <input type="time" data-testid="school-hours-start" value={settings.school_hours_start || "08:00"}
            onChange={(e) => onChange({ ...settings, school_hours_start: e.target.value })}
            className="input-base text-sm" style={{ padding: "4px 8px", width: "auto" }} />
          <span className="text-sm text-[#000000]">to</span>
          <input type="time" data-testid="school-hours-end" value={settings.school_hours_end || "17:00"}
            onChange={(e) => onChange({ ...settings, school_hours_end: e.target.value })}
            className="input-base text-sm" style={{ padding: "4px 8px", width: "auto" }} />
          <span className="text-xs text-[#000000]">({settings.school_timezone || "America/Edmonton"})</span>
        </div>
      )}
    </>
  );
}

function SuperAdminView() {
  const [data, setData] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [flagWords, setFlagWords] = useState("");
  const [officerId, setOfficerId] = useState("");
  const [mentorId, setMentorId] = useState("");
  const [flags, setFlags] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/messaging-settings").then(r => {
      setData(r.data);
      setDefaults(r.data.defaults);
      setFlagWords((r.data.flag_words || []).join(", "));
      setOfficerId(r.data.global_safety_officer_id || "");
      setMentorId(r.data.sos_mentor_id || "");
    }).catch(e => toast.error(formatApiError(e)));
    api.get("/admin/message-flags").then(r => setFlags(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/messaging-settings", {
        defaults,
        flag_words: flagWords.split(",").map(w => w.trim()).filter(Boolean),
        global_safety_officer_id: officerId || "",
        sos_mentor_id: mentorId || "",
      });
      toast.success("Messaging defaults saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const resolve = async (fid) => {
    try {
      await api.post(`/admin/message-flags/${fid}/resolve`);
      setFlags(fs => fs.map(f => f.id === fid ? { ...f, resolved: true } : f));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [flagFilter, setFlagFilter] = useState("all");

  if (!data) return <div className="text-sm text-[#000000] p-4">Loading…</div>;
  const openFlags = flags.filter(f => !f.resolved);
  const shownFlags = flags.filter(f =>
    flagFilter === "active" ? !f.resolved : flagFilter === "resolved" ? f.resolved : true
  );

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-[#C4C8CE] bg-white p-5" data-testid="messaging-defaults-card">
        <h3 className="font-display font-bold text-lg flex items-center gap-2"><ChatCircleText size={20} weight="duotone" className="hidden sm:inline shrink-0" /> System-wide messaging defaults</h3>
        <p className="text-xs text-[#000000] mb-3">The baseline every new board inherits. Boards can override for their own students.</p>
        <Toggles settings={defaults} onChange={setDefaults} presets={data.presets} />
      </div>

      <div className="rounded-2xl border border-[#C4C8CE] bg-white p-5" data-testid="sos-mentor-card">
        <h3 className="font-display font-bold text-lg flex items-center gap-2"><Heart size={20} weight="fill" color="#DC2626" /> Confidential Mentor (SOS recipient)</h3>
        <p className="text-xs text-[#000000] mb-3">Who receives private SOS messages sent from the ❤️ button. When unset, every Super Admin receives them.</p>
        <Select value={mentorId || "__none__"} onValueChange={(v) => setMentorId(v === "__none__" ? "" : v)}>
          <SelectTrigger data-testid="sos-mentor-select" className="max-w-sm"><SelectValue placeholder="All Super Admins (default)" /></SelectTrigger>
          <SelectContent className="max-h-64">
            <SelectItem value="__none__">All Super Admins (default)</SelectItem>
            {data.staff.map(s => <SelectItem key={s.id} value={s.id}>{s.name} · {roleLabel(s.role) || s.role}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-2xl border border-[#C4C8CE] bg-white p-5" data-testid="safety-officer-card">
        <h3 className="font-display font-bold text-lg flex items-center gap-2"><ShieldStar size={20} weight="duotone" color="#DC2626" /> Global Safety Officer</h3>
        <p className="text-xs text-[#000000] mb-3">Receives a parallel copy of every SOS. No other admin powers.</p>
        <Select value={officerId || "__none__"} onValueChange={(v) => setOfficerId(v === "__none__" ? "" : v)}>
          <SelectTrigger data-testid="global-officer-select" className="max-w-sm"><SelectValue placeholder="No global safety officer" /></SelectTrigger>
          <SelectContent className="max-h-64">
            <SelectItem value="__none__">None</SelectItem>
            {data.staff.map(s => <SelectItem key={s.id} value={s.id}>{s.name} · {roleLabel(s.role) || s.role}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-2xl border border-[#C4C8CE] bg-white p-5" data-testid="flag-words-card">
        <h3 className="font-display font-bold text-lg flex items-center gap-2"><Flag size={20} weight="duotone" color="#EA580C" /> Auto-flag words &amp; phrases</h3>
        <p className="text-xs text-[#000000] mb-2">Any message containing these gets flagged to you instantly (comma-separated).</p>
        <textarea data-testid="flag-words-input" value={flagWords} onChange={e => setFlagWords(e.target.value)} rows={3}
          className="w-full rounded-xl border border-[#C4C8CE] p-3 text-sm" />
      </div>

      <button data-testid="messaging-save-button" className="btn-primary" disabled={saving} onClick={save}>
        {saving ? "Saving…" : "Save messaging settings"}
      </button>

      <div className="rounded-2xl border border-[#C4C8CE] bg-white p-5" data-testid="flagged-messages-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="font-display font-bold text-lg flex items-center gap-2">
            <WarningCircle size={20} weight="duotone" color="#DC2626" /> Flagged &amp; reported messages
            {openFlags.length > 0 && <span className="px-2 py-0.5 rounded-full bg-[#DC2626] text-white text-xs">{openFlags.length} open</span>}
          </h3>
          {flags.length > 0 && (
            <Select value={flagFilter} onValueChange={setFlagFilter}>
              <SelectTrigger className="w-36 h-8 text-xs" data-testid="flags-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="flags-filter-all">Show all</SelectItem>
                <SelectItem value="active" data-testid="flags-filter-active">Active</SelectItem>
                <SelectItem value="resolved" data-testid="flags-filter-resolved">Resolved</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
        {flags.length === 0 ? (
          <p className="text-sm text-[#000000] mt-2">Nothing flagged — the safety net is quiet.</p>
        ) : shownFlags.length === 0 ? (
          <p className="text-sm text-[#000000] mt-2" data-testid="flags-filter-empty">Nothing matches this filter.</p>
        ) : (
          <div className="mt-3 flex flex-col gap-2">
            {shownFlags.map(f => (
              <div key={f.id} className={`py-3 px-3 rounded-xl ${f.resolved ? "opacity-50 bg-[#F8F8F8] border border-[#EDEEF0]" : "bg-[rgba(204,255,0,0.25)] border border-[#A3E635]"}`} data-testid={`flag-row-${f.id}`}>
                <div className="flex items-center gap-2 flex-wrap text-xs">
                  <span className={`px-2 py-0.5 rounded-full font-bold ${f.kind === "auto" ? "bg-[#FEF3C7] text-[#92400E]" : "bg-[#FEE2E2] text-[#991B1B]"}`}>
                    {f.kind === "auto" ? "AUTO-FLAG" : "REPORTED"}
                  </span>
                  <span className="font-semibold">{f.sender_name}</span>
                  {f.kind === "auto" && f.matched?.length > 0 && <span className="text-[#B45309]">matched: {f.matched.join(", ")}</span>}
                  {f.kind === "report" && <span className="text-[#000000]">by {f.reported_by} — "{f.reason}"</span>}
                  <span className="text-[#000000] ml-auto">{new Date(f.created_at).toLocaleString()}</span>
                </div>
                <div className="text-sm mt-1 bg-[#F9FAFB] rounded-lg px-3 py-2 border border-[#EDEEF0]">{f.body}</div>
                {!f.resolved && (
                  <button data-testid={`flag-resolve-${f.id}`} className="btn-ghost text-xs mt-1.5 flex items-center gap-1 text-[#166534]" onClick={() => resolve(f.id)}>
                    <Check size={12} weight="bold" /> Mark resolved
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BoardAdminView({ boardId }) {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState({});
  const [officerIds, setOfficerIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sosConfirm, setSosConfirm] = useState(false);
  const [sosReason, setSosReason] = useState("");

  const load = () => { api.get(`/boards/${boardId}/messaging-settings`).then(r => {
    setData(r.data);
    setSettings(r.data.settings);
    setOfficerIds(r.data.safety_officer_ids || []);
  }).catch(e => toast.error(formatApiError(e))); };
  useEffect(load, [boardId]); // eslint-disable-line

  const doSave = async (reason) => {
    setSaving(true);
    try {
      await api.put(`/boards/${boardId}/messaging-settings`, {
        settings, safety_officer_ids: officerIds, sos_disable_reason: reason || null,
      });
      toast.success("Board messaging settings saved");
      setSosConfirm(false);
      setSosReason("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const save = () => {
    const wasOn = data?.settings?.sos_enabled !== false;
    if (wasOn && settings.sos_enabled === false) { setSosConfirm(true); return; }
    doSave();
  };

  if (!data) return <div className="text-sm text-[#000000] p-4">Loading…</div>;

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-[#C4C8CE] bg-white p-5" data-testid="board-messaging-card">
        <h3 className="font-display font-bold text-lg flex items-center gap-2"><ChatCircleText size={20} weight="duotone" className="hidden sm:inline shrink-0" /> Messaging channels — {data.board_name}</h3>
        <p className="text-xs text-[#000000] mb-3">Pick a preset or fine-tune each channel for your board's students and teachers.</p>
        <Toggles settings={settings} onChange={setSettings} presets={data.presets} />
      </div>

      <div className="rounded-2xl border border-[#C4C8CE] bg-white p-5" data-testid="board-officers-card">
        <h3 className="font-display font-bold text-lg flex items-center gap-2"><ShieldStar size={20} weight="duotone" color="#DC2626" /> Safety Officers</h3>
        <p className="text-xs text-[#000000] mb-3">SOS messages from your students route to these people (the Super Admin always gets a parallel copy).</p>
        <div className="flex flex-col gap-1.5">
          {data.staff.map(s => (
            <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`officer-option-${s.id}`}>
              <input type="checkbox" checked={officerIds.includes(s.id)}
                onChange={(e) => setOfficerIds(ids => e.target.checked ? [...ids, s.id] : ids.filter(i => i !== s.id))} />
              {s.name} · {roleLabel(s.role) || s.role}
            </label>
          ))}
          {data.staff.length === 0 && <div className="text-xs text-[#000000]">No staff in this board yet.</div>}
        </div>
      </div>

      <button data-testid="board-messaging-save" className="btn-primary" disabled={saving} onClick={save}>
        {saving ? "Saving…" : "Save board settings"}
      </button>

      <Dialog open={sosConfirm} onOpenChange={setSosConfirm}>
        <DialogContent className="max-w-md bg-white" aria-describedby={undefined} data-testid="sos-disable-confirm-dialog">
          <DialogHeader><DialogTitle className="font-display text-[#991B1B]">Disable in-app crisis resources?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#000000]">
            You are about to disable in-app crisis resource access for <b>{data.student_count} student{data.student_count === 1 ? "" : "s"}</b> under this board.
            The Help (❤️) icon will be removed. Students will need to access crisis resources outside this app. Are you sure?
          </p>
          <textarea data-testid="sos-disable-reason-input" value={sosReason} onChange={e => setSosReason(e.target.value)} rows={2}
            placeholder="Reason (required, audit-logged)" className="w-full rounded-xl border border-[#C4C8CE] p-3 text-sm" />
          <div className="flex gap-2 justify-end">
            <button className="btn-secondary" onClick={() => setSosConfirm(false)}>Cancel</button>
            <button data-testid="sos-disable-confirm-button" className="btn-primary bg-[#DC2626] border-[#DC2626]" disabled={!sosReason.trim() || saving}
              onClick={() => doSave(sosReason.trim())}>
              Yes, disable SOS
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function MessagingSettingsCard() {
  const { user } = useAuth();
  if (user?.role === "super_admin") return <SuperAdminView />;
  if (user?.role === "school_board_admin" && user?.school_board_id) return <BoardAdminView boardId={user.school_board_id} />;
  return <div className="text-sm text-[#000000] p-4">Messaging settings are managed by the Super Admin or your Board Admin.</div>;
}
